import { useMemo } from "react";

import { useDigitalSizerClient } from "./useDigitalSizerApi";

/**
 * Task List Manager API — mirrors digital_sizer's `api/taskLists.ts`, now wired
 * onto the LOS client (`dsHttp` → eastview-los), per the digital-sizer base-URL
 * removal.
 *
 * ⚠️ TBD: eastview-os has NO `/task-lists/*` manager routes yet. The LOS has
 * loan-scoped checklist endpoints (`/underwriting/tasklist_templates`,
 * `/create_uw_tasklist/*`, `/update_task/*`, etc. in checklists.py) — a different
 * API shape. Every call below 404s until the backend adds the manager routes.
 *   GET    /task-lists/options
 *   GET    /task-lists/tasks?product=&review_mode=
 *   GET    /task-lists/tasks/:id
 *   POST   /task-lists/tasks
 *   PATCH  /task-lists/tasks/:id
 *   DELETE /task-lists/tasks/:id
 *   GET    /task-lists/tasks/:id/audit
 *
 * Types (for reference — JS has no enforcement):
 *   Task: { id, taskText, sourceDocument, orderOfReview, productApplicability,
 *     reviewApplicability, taskStatus, loanLevelConditionPolicy,
 *     riskBeingMitigated, reviewer, authorityToWaive, clearingParty,
 *     compensatingFactors[], relatedDocuments[], relatedTaskIds[],
 *     conditionIds[], documentConditions[], last_edited_by, last_edited_at,
 *     created_at, updated_at }
 */
export const useTaskListsHook = () => {
  const client = useDigitalSizerClient();

  return useMemo(() => {
    const getTaskOptions = async () => {
      const res = await client.get("/task-lists/options");
      return res.data;
    };

    const listTasks = async (product, reviewMode) => {
      const params = {};
      if (product) params.product = product;
      if (reviewMode) params.review_mode = reviewMode;
      const res = await client.get("/task-lists/tasks", { params });
      return Array.isArray(res.data) ? res.data : [];
    };

    const getTask = async (id) => {
      const res = await client.get(`/task-lists/tasks/${id}`);
      return res.data;
    };

    const createTask = async (data) => {
      const res = await client.post("/task-lists/tasks", data);
      return res.data;
    };

    const updateTask = async (id, data) => {
      const res = await client.patch(`/task-lists/tasks/${id}`, data);
      return res.data;
    };

    const deleteTask = async (id) => {
      await client.delete(`/task-lists/tasks/${id}`);
    };

    const getTaskAudit = async (id) => {
      const res = await client.get(`/task-lists/tasks/${id}/audit`);
      return Array.isArray(res.data) ? res.data : [];
    };

    return {
      getTaskOptions,
      listTasks,
      getTask,
      createTask,
      updateTask,
      deleteTask,
      getTaskAudit,
    };
  }, [client]);
};

export default useTaskListsHook;
